/**
 * Welle B (Punkt 15) — „Dateimenü" für einen Link im MD-Editor: der Anzeige-Typ
 * eines Datei-/URL-Links ist wählbar — **Link**, **Button** oder **Vorschau** —
 * jeweils mit seinen Anpassungen:
 *   - Link:    Beschriftung optional (leer ⇒ die Ziel-URL wird angezeigt)
 *   - Button:  Beschriftung PFLICHT
 *   - Vorschau: Anzeige-Modus (Original · Einpassen · Füllen), analog zu Bildern
 *
 * Der Dialog liefert nur die Auswahl zurück; das Einfügen/Ersetzen im Dokument
 * (Markdown bleibt die eine Quelle der Wahrheit) macht der Aufrufer.
 */

import { useState } from "react";

export type LinkType = "link" | "button" | "preview";
export type PreviewMode = "original" | "fit" | "fill";

export interface LinkSettings {
  type: LinkType;
  href: string;
  label: string;
  mode: PreviewMode;
}

export const LinkSettingsDialog = ({
  initial,
  lockHref,
  onClose,
  onApply,
}: {
  initial: LinkSettings;
  // Beim Bearbeiten eines bestehenden Datei-Links ist das Ziel fix (nur der
  // Anzeige-Typ ändert sich); beim Neuanlegen ist das Ziel-Feld editierbar.
  lockHref?: boolean;
  onClose: () => void;
  onApply: (settings: LinkSettings) => void;
}) => {
  const [type, setType] = useState<LinkType>(initial.type);
  const [href, setHref] = useState(initial.href);
  const [label, setLabel] = useState(initial.label);
  const [mode, setMode] = useState<PreviewMode>(initial.mode);
  const [error, setError] = useState<string | null>(null);

  const apply = () => {
    const h = href.trim();
    if (!h) {
      setError("Bitte ein Ziel angeben (URL oder Dokument-Pfad).");
      return;
    }
    if (type === "button" && !label.trim()) {
      setError("Ein Button braucht eine Beschriftung.");
      return;
    }
    onApply({ type, href: h, label: label.trim(), mode });
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Link-Darstellung</h2>

        <label className="ryfe-field-label">
          Anzeige-Typ
          <select
            className="ryfe-select"
            value={type}
            aria-label="Anzeige-Typ"
            onChange={(e) => setType(e.target.value as LinkType)}
          >
            <option value="link">Link</option>
            <option value="button">Button</option>
            <option value="preview">Vorschau</option>
          </select>
        </label>

        {!lockHref && (
          <label className="ryfe-field-label">
            Ziel (URL oder /md/… /list/… /board/…)
            <input
              className="ryfe-field-input"
              value={href}
              aria-label="Ziel"
              placeholder="https://… oder /md/<id>"
              onChange={(e) => setHref(e.target.value)}
            />
          </label>
        )}

        {type !== "preview" && (
          <label className="ryfe-field-label">
            Beschriftung
            {type === "button" ? " (Pflicht)" : " (optional)"}
            <input
              className="ryfe-field-input"
              value={label}
              aria-label="Beschriftung"
              placeholder={
                type === "button" ? "z. B. Öffnen" : "leer ⇒ Ziel-URL"
              }
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
        )}

        {type === "preview" && (
          <>
            <label className="ryfe-field-label">
              Name (im Vorschau-Balken)
              <input
                className="ryfe-field-input"
                value={label}
                aria-label="Vorschau-Name"
                placeholder="z. B. Handover"
                onChange={(e) => setLabel(e.target.value)}
              />
            </label>
            <label className="ryfe-field-label">
              Anzeigeverhalten
              <select
                className="ryfe-select"
                value={mode}
                aria-label="Anzeigeverhalten"
                onChange={(e) => setMode(e.target.value as PreviewMode)}
              >
                <option value="original">Original</option>
                <option value="fit">Einpassen</option>
                <option value="fill">Füllen</option>
              </select>
            </label>
          </>
        )}

        {error && (
          <div className="ryfe-auth__error" role="alert">
            {error}
          </div>
        )}

        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            data-testid="link-settings-apply"
            onClick={apply}
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
};

/** Baut das Markdown-Token aus den gewählten Einstellungen. */
export const linkMarkdown = (s: LinkSettings): string => {
  const label = s.label || s.href;
  if (s.type === "button") {
    return `[${label}](${s.href} "button")`;
  }
  if (s.type === "preview") {
    return `[${s.label || "Vorschau"}](${s.href} "preview:${s.mode}")`;
  }
  // Link: Beschriftung optional — leer ⇒ die URL selbst als Text.
  return `[${label}](${s.href})`;
};
